import { apiRequest } from '../utils/api'
import { BlogPost, BlogPostsResponse, GenerationResponse, VerificationResult, ContentPage } from '../types'

// Blog API functions
export const blogApi = {
  // Get all blog posts with optional filtering
  getPosts: async (params?: {
    page?: number
    per_page?: number
    category?: string
    tag?: string
    search?: string
  }) => {
    const queryParams = new URLSearchParams()
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString())
    if (params?.category) queryParams.append('category', params.category)
    if (params?.tag) queryParams.append('tag', params.tag)
    if (params?.search) queryParams.append('search', params.search)
    
    const url = `/blog/posts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return apiRequest<BlogPostsResponse>('GET', url)
  },

  // Get a single blog post by slug
  getPost: async (slug: string) => {
    return apiRequest<{ post: BlogPost }>('GET', `/blog/posts/${slug}`)
  },

  // Get blog categories
  getCategories: async () => {
    return apiRequest<{ categories: string[] }>('GET', '/blog/categories')
  },

  // Get blog tags
  getTags: async () => {
    return apiRequest<{ tags: string[] }>('GET', '/blog/tags')
  },

  // Get featured posts
  getFeaturedPosts: async (limit = 5) => {
    return apiRequest<{ posts: BlogPost[] }>('GET', `/blog/featured?limit=${limit}`)
  },

  // Create a new blog post (admin function)
  createPost: async (postData: Partial<BlogPost>) => {
    return apiRequest<{ post: BlogPost }>('POST', '/blog/posts', postData)
  },

  // Update a blog post (admin function)
  updatePost: async (postId: number, postData: Partial<BlogPost>) => {
    return apiRequest<{ post: BlogPost }>('PUT', `/blog/posts/${postId}`, postData)
  },

  // Delete a blog post (admin function)
  deletePost: async (postId: number) => {
    return apiRequest<{ message: string }>('DELETE', `/blog/posts/${postId}`)
  }
}

// Generator API functions
export const generatorApi = {
  // Generate llm.txt content
  generate: async (formData: any) => {
    return apiRequest<GenerationResponse>('POST', '/generator/generate', formData)
  },

  // Get preview of llm.txt content
  preview: async (formData: any) => {
    return apiRequest<{ preview: string }>('POST', '/generator/preview', formData)
  },

  // Download llm.txt file
  download: async (content: string) => {
    return apiRequest<Blob>('POST', '/generator/download', { content })
  }
}

// Auto Mode API functions
export const autoModeApi = {
  // Validate API key for specified provider
  validateApiKey: async (provider: string, apiKey: string) => {
    return apiRequest<{ valid: boolean; provider: string }>('POST', '/auto-mode/validate-api-key', {
      provider,
      api_key: apiKey
    })
  },

  // Crawl website and extract content
  crawlWebsite: async (url: string, mode: 'quick' | 'indepth' = 'quick', sitemapUrl?: string) => {
    const payload: any = { url, mode }
    if (sitemapUrl) {
      payload.sitemap_url = sitemapUrl
    }
    return apiRequest<{ crawling_result: any }>('POST', '/auto-mode/crawl-website', payload)
  },

  // Generate llm.txt content using AI
  generateAuto: async (provider: string, model: string, apiKey: string, crawlingResult: any) => {
    return apiRequest<GenerationResponse & { provider: string; tokens_used?: number }>('POST', '/auto-mode/generate-auto', {
      provider,
      model,
      api_key: apiKey,
      crawling_result: crawlingResult
    })
  },

  // Complete auto mode workflow
  processWebsite: async (url: string, provider: string, model: string, apiKey: string) => {
    return apiRequest<{
      crawling_result: any;
      content: string;
      provider: string;
      tokens_used?: number;
      filename: string;
    }>('POST', '/auto-mode/process-website', {
      url,
      provider,
      model,
      api_key: apiKey
    })
  },

  // Health check for auto mode services
  healthCheck: async () => {
    return apiRequest<{ status: string; services: Record<string, string> }>('GET', '/auto-mode/health')
  }
}

// Verification API functions
export const verificationApi = {
  // Verify llm.txt file accessibility
  verify: async (url: string) => {
    return apiRequest<VerificationResult>('POST', '/verification/check', { url })
  },

  // Validate llm.txt content
  validate: async (content: string) => {
    return apiRequest<VerificationResult>('POST', '/verification/validate', { content })
  }
}

// Content API functions
export const contentApi = {
  // Get static content page
  getPage: async (slug: string) => {
    return apiRequest<{ page: ContentPage }>('GET', `/content/pages/${slug}`)
  },

  // Get all available pages
  getAllPages: async () => {
    return apiRequest<{ pages: Array<{ slug: string; title: string; meta_description: string }> }>('GET', '/content/pages')
  }
}
